import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * WebSocketIframe Component
 * Displays an iframe for AI Human WebSocket interface with customizable styling
 * 
 * @param {string} url - The URL to load in the iframe
 * @param {string} brandColor - The brand color for border styling
 * @param {object} styles - Custom styling configuration from backend
 */
const WebSocketIframe = ({ url, brandColor, styles = {} }) => {
  // Default styles
  const defaultStyles = {
    bottom: 20,
    left: 50,
    right: null,
    width: 800,
    height: 500,
    padding: 12,
    borderRadius: 16,
    borderWidth: 4,
    zIndex: 1000
  };

  // Merge provided styles with defaults
  const mergedStyles = { ...defaultStyles, ...styles };

  // Convert px to vw for width (assuming 1920px viewport as base)
  const widthInVw = useMemo(() => {
    const baseViewport = 1920;
    return (mergedStyles.width / baseViewport) * 100;
  }, [mergedStyles.width]);

  // Convert px to vw for bottom
  const bottomInVw = useMemo(() => {
    const baseViewport = 1080; // Base height for vertical calculations
    return (mergedStyles.bottom / baseViewport) * 100;
  }, [mergedStyles.bottom]);

  // Convert px to vw for right (if used)
  const rightInVw = useMemo(() => {
    if (mergedStyles.right === null || mergedStyles.right === undefined) return null;
    const baseViewport = 1920;
    return (mergedStyles.right / baseViewport) * 100;
  }, [mergedStyles.right]);

  // Convert px to vw for left (if used as offset, not percentage)
  const leftInVw = useMemo(() => {
    // If left is being used as percentage (for centering), keep it as is
    // Otherwise convert to vw
    if (mergedStyles.right !== null && mergedStyles.right !== undefined) {
      return null; // Not using left when right is set
    }
    // Left is used as percentage for centering, so keep it
    return mergedStyles.left;
  }, [mergedStyles.left, mergedStyles.right]);

  // Calculate positioning
  const positioning = useMemo(() => {
    const pos = {
      bottom: `${bottomInVw}vh`,
      zIndex: mergedStyles.zIndex
    };

    if (rightInVw !== null) {
      // If right is specified, use it in vw
      pos.right = `${rightInVw}vw`;
    } else {
      // Otherwise use left percentage with transform for centering
      pos.left = `${leftInVw}%`;
      pos.transform = 'translateX(-50%)';
    }

    return pos;
  }, [bottomInVw, leftInVw, rightInVw, mergedStyles.zIndex]);

  // Container styles
  const containerStyle = {
    position: 'fixed',
    ...positioning,
    width: `${widthInVw}vw`,
    maxWidth: `${mergedStyles.width}px`,
    background: 'rgba(255, 255, 255, 0.98)',
    borderRadius: `${mergedStyles.borderRadius}px`,
    padding: `${mergedStyles.padding}px`,
    border: `${mergedStyles.borderWidth}px solid ${brandColor}`,
    boxShadow: `0 10px 40px rgba(0, 0, 0, 0.3), 0 0 0 2px ${brandColor}30`,
    transition: 'all 0.3s ease'
  };

  // Iframe styles
  const iframeStyle = {
    width: '100%',
    height: `${mergedStyles.height}px`,
    border: 'none',
    background: 'white',
    display: 'block',
    borderRadius: `${mergedStyles.borderRadius - 4}px` // Slightly smaller than container
  };

  // Responsive adjustments
  const responsiveStyles = `
    @media (max-width: 1024px) {
      .websocket-iframe-container {
        width: ${Math.min(widthInVw * 1.1, 92)}vw !important;
        max-width: ${mergedStyles.width * 0.9}px !important;
      }
      
      .websocket-iframe {
        height: ${mergedStyles.height * 0.9}px !important;
      }
    }

    @media (max-width: 768px) {
      .websocket-iframe-container {
        width: 95vw !important;
        max-width: none !important;
        bottom: ${Math.max(bottomInVw - 0.5, 1)}vh !important;
        padding: ${Math.max(mergedStyles.padding - 2, 8)}px !important;
      }
      
      .websocket-iframe {
        height: ${mergedStyles.height * 0.75}px !important;
      }
    }

    @media (max-width: 480px) {
      .websocket-iframe-container {
        width: 96vw !important;
        bottom: ${Math.max(bottomInVw - 1, 0.5)}vh !important;
        padding: ${Math.max(mergedStyles.padding - 4, 6)}px !important;
      }
      
      .websocket-iframe {
        height: ${mergedStyles.height * 0.64}px !important;
      }
    }
  `;

  if (!url) {
    return null;
  }

  return (
    <>
      <style>{responsiveStyles}</style>
      <div 
        className="websocket-iframe-container"
        style={containerStyle}
      >
        <iframe
          src={url}
          title="AI Human WebSocket Interface"
          className="websocket-iframe"
          style={iframeStyle}
          allow="microphone; camera; autoplay"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
        />
      </div>
    </>
  );
};

WebSocketIframe.propTypes = {
  url: PropTypes.string.isRequired,
  brandColor: PropTypes.string.isRequired,
  styles: PropTypes.shape({
    bottom: PropTypes.number,
    left: PropTypes.number,
    right: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    padding: PropTypes.number,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    zIndex: PropTypes.number
  })
};

WebSocketIframe.defaultProps = {
  styles: {}
};

export default WebSocketIframe;
